import type { SagaIterator } from 'redux-saga'
import { call, put, select } from 'typed-redux-saga'
import { Actions } from '~/actions'
import type { State } from '~/types'
import { sleep } from '~/utils'
import {
	RefreshTokenRejected,
	reauthenticate,
	reauthInProgress,
	refreshAccessToken,
	storeTokens
} from '~/utils/spotifyAuth'

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export function* spotifyFetch<T>(url: string, options: RequestInit = {}, apiToken?: string): SagaIterator<T | null> {
	const userToken = yield* select((state: State) => state.user?.spotifyToken)
	const token = apiToken || userToken || localStorage.getItem('token')

	if (!token) {
		throw new Error(`${spotifyFetch.name}:Token not found`)
	}

	const headers = new Headers({ Authorization: `Bearer ${token}` })
	const response = yield* call(fetch, `https://api.spotify.com/v1/${url}`, { headers, ...options })
	let body: any = null
	if (response.status !== 204) {
		const text = yield* call(response.text.bind(response))
		if (text) {
			try {
				body = JSON.parse(text)
			} catch {
				// Some Spotify endpoints (player controls, library writes) reply 200
				// with an empty or non-JSON body — treat that the same as 204.
			}
		}
	}

	switch (response.status) {
		case 200:
		case 304:
			return body as T

		case 204:
			return null

		case 401: {
			const refreshToken = localStorage.getItem('refresh_token')
			if (refreshToken) {
				try {
					const tokens = yield* call(refreshAccessToken, refreshToken)
					storeTokens(tokens)
					yield* put(Actions.tokenRefreshed(tokens.access_token))
					const next = yield* call(spotifyFetch, url, options, tokens.access_token)
					return next as T | null
				} catch (e) {
					console.error('Token refresh failed:', e)
					// Only act on a real rejection. Transient errors (offline, DNS,
					// Spotify 5xx) surface as a fetch failure — the next call retries.
					if (e instanceof RefreshTokenRejected) {
						// The refresh token is dead. Rather than dumping the user at a
						// login button, bounce through Spotify's authorize endpoint —
						// it redirects straight back with a fresh code when their
						// Spotify session is alive (no consent screen). Guard against
						// loops: if a bounce is already underway, log out for real.
						if (reauthInProgress()) {
							yield* put(Actions.logout())
						} else {
							yield* call(reauthenticate) // navigates away
							return null
						}
					}
					throw e instanceof Error ? e : new Error(String(e))
				}
			}
			yield* put(Actions.logout())
			break
		}
		case 429: {
			const waitTime = Number.parseInt(response.headers.get('Retry-After') || '10', 10)
			yield* put(Actions.startTimer(waitTime))
			yield* call(sleep, waitTime * 1000)
			const next = yield* call(spotifyFetch, url, options, token)
			return next as T | null
		}
		default:
			throw new Error(body?.error?.message ?? `${response.status} ${response.statusText}`)
	}
	return null
}
